import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/ProfileDetail.css';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? ''
  : process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Icon = {
  arrowLeft: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  arrowRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  trash: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  building: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" /><line x1="9" y1="14" x2="15" y2="14" />
    </svg>
  ),
  fileText: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  upload: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  sparkle: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.9 5.8L20 10.7l-5.8 1.9L12 18l-1.9-5.4L4 10.7l6.1-1.9z" />
    </svg>
  )
};

const ROUNDS = ['Phone Screen', 'Round 1', 'Round 2', 'Round 3', 'Round 4', 'Technical', 'Behavioral', 'Final', 'Other'];

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Locally persist created interviews so they appear immediately,
// even before any question is saved under them.
const storageKey = (profile) => `fqa_interviews_${profile?.id ?? 'guest'}`;

const loadStoredInterviews = (profile) => {
  try {
    return JSON.parse(localStorage.getItem(storageKey(profile))) || [];
  } catch {
    return [];
  }
};

const saveStoredInterview = (profile, companyName, interviewRound) => {
  const list = loadStoredInterviews(profile);
  const exists = list.some(i => i.companyName === companyName && i.interviewRound === interviewRound);
  if (!exists) {
    list.push({ companyName, interviewRound });
    localStorage.setItem(storageKey(profile), JSON.stringify(list));
  }
};

const removeStoredInterview = (profile, companyName, interviewRound) => {
  const list = loadStoredInterviews(profile).filter(
    i => !(i.companyName === companyName && i.interviewRound === interviewRound)
  );
  localStorage.setItem(storageKey(profile), JSON.stringify(list));
};

function ProfileDetail({ profile, onLogout, onInterviewSelect, onBack }) {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddInterview, setShowAddInterview] = useState(false);
  const [formData, setFormData] = useState({ companyName: '', interviewRound: '' });

  // Resume state
  const [resume, setResume] = useState({ hasResume: false, filename: null, summary: null });
  const [uploadingResume, setUploadingResume] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const fileInputRef = React.useRef(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchInterviews();
    fetchResume();
    const handleQuestionAdded = () => fetchInterviews();
    window.addEventListener('questionAdded', handleQuestionAdded);
    return () => window.removeEventListener('questionAdded', handleQuestionAdded);
  }, [profile]);

  const fetchResume = async () => {
    if (!profile?.id) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/profiles/${profile.id}/resume`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setResume(res.data.data || { hasResume: false });
    } catch (error) {
      // Non-fatal
      console.error('Error fetching resume:', error);
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!profile?.id) {
      alert('Save the profile first before attaching a resume.');
      return;
    }
    const form = new FormData();
    form.append('resume', file);
    try {
      setUploadingResume(true);
      const res = await axios.post(`${API_BASE_URL}/api/profiles/${profile.id}/resume`, form, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setResume(res.data.data || { hasResume: true, filename: file.name });
    } catch (error) {
      console.error('Error uploading resume:', error);
      alert(error.response?.data?.message || 'Error uploading resume');
    } finally {
      setUploadingResume(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleResumeDelete = async () => {
    if (!profile?.id) return;
    if (!window.confirm('Remove the attached resume?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/profiles/${profile.id}/resume`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setResume({ hasResume: false, filename: null, summary: null });
      setShowSummary(false);
    } catch (error) {
      console.error('Error removing resume:', error);
      alert('Error removing resume');
    }
  };

  const fetchInterviews = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/questions/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const interviewMap = new Map();

      // Seed with locally created interviews so they show even with 0 questions
      loadStoredInterviews(profile).forEach(({ companyName, interviewRound }) => {
        const key = `${companyName}|${interviewRound}`;
        interviewMap.set(key, { companyName, interviewRound, count: 0, questions: [] });
      });

      response.data.data.forEach(question => {
        if (question.companyName && question.interviewRound) {
          const key = `${question.companyName}|${question.interviewRound}`;
          if (!interviewMap.has(key)) {
            interviewMap.set(key, {
              companyName: question.companyName,
              interviewRound: question.interviewRound,
              count: 0,
              questions: []
            });
          }
          interviewMap.get(key).count += 1;
          interviewMap.get(key).questions.push(question);
        }
      });

      setInterviews(Array.from(interviewMap.values()));
    } catch (error) {
      console.error('Error fetching interviews:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddInterview = async (e) => {
    e.preventDefault();
    if (!formData.companyName.trim() || !formData.interviewRound.trim()) {
      alert('Please enter both company name and interview round');
      return;
    }
    try {
      setIsLoading(true);
      // Persist the interview locally so it appears in the list immediately
      saveStoredInterview(profile, formData.companyName.trim(), formData.interviewRound.trim());
      onInterviewSelect(profile.id, profile, formData.companyName.trim(), formData.interviewRound.trim());
      setFormData({ companyName: '', interviewRound: '' });
      setShowAddInterview(false);
      navigate('/search');
    } catch (error) {
      console.error('Error:', error);
      alert('Error starting interview');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectInterview = (companyName, interviewRound) => {
    onInterviewSelect(profile.id, profile, companyName, interviewRound);
    navigate('/search');
  };

  const handleRemoveInterview = (e, companyName, interviewRound) => {
    e.stopPropagation();
    if (!window.confirm(`Remove "${companyName} - ${interviewRound}" interview?`)) return;
    removeStoredInterview(profile, companyName, interviewRound);
    fetchInterviews();
  };

  const totalQuestions = interviews.reduce((sum, i) => sum + i.count, 0);

  return (
    <div className="pd-page">
      {/* Header */}
      <header className="pd-header">
        <div className="pd-header-left">
          <button className="pd-back" onClick={onBack}>{Icon.arrowLeft}<span>Profiles</span></button>
          <div className="pd-header-profile">
            <div className="pd-avatar">{getInitials(profile.studentName)}</div>
            <div>
              <h1>{profile.studentName}</h1>
              <p>{profile.course} · {profile.yearsOfExperience} yrs · {profile.currentDesignation || 'N/A'}</p>
            </div>
          </div>
        </div>
        <button className="pd-btn pd-btn-ghost" onClick={onLogout}>{Icon.logout}<span>Logout</span></button>
      </header>

      <main className="pd-content">
        {/* Left: profile info */}
        <aside className="pd-sidebar">
          <div className="pd-panel">
            <h3 className="pd-panel-title">Profile Information</h3>
            <ul className="pd-info-list">
              <li><span className="pd-info-label">Name</span><span className="pd-info-value">{profile.studentName}</span></li>
              <li><span className="pd-info-label">Course</span><span className="pd-info-value">{profile.course}</span></li>
              <li><span className="pd-info-label">Experience</span><span className="pd-info-value">{profile.yearsOfExperience} years</span></li>
              <li><span className="pd-info-label">Designation</span><span className="pd-info-value">{profile.currentDesignation || 'Not specified'}</span></li>
              {profile.phoneNumber && <li><span className="pd-info-label">Phone</span><span className="pd-info-value">{profile.phoneNumber}</span></li>}
            </ul>
          </div>

          <div className="pd-stats">
            <div className="pd-stat">
              <span className="pd-stat-num">{interviews.length}</span>
              <span className="pd-stat-label">Interviews</span>
            </div>
            <div className="pd-stat">
              <span className="pd-stat-num">{totalQuestions}</span>
              <span className="pd-stat-label">Questions</span>
            </div>
          </div>

          {/* Resume panel */}
          <div className="pd-panel pd-resume">
            <div className="pd-resume-head">
              <h3 className="pd-panel-title">Resume</h3>
              {resume.hasResume && <span className="pd-resume-badge">{Icon.check}Attached</span>}
            </div>

            {resume.hasResume ? (
              <>
                <div className="pd-resume-file">
                  <div className="pd-resume-file-icon">{Icon.fileText}</div>
                  <span className="pd-resume-file-name" title={resume.filename}>{resume.filename || 'Resume'}</span>
                </div>
                <p className="pd-resume-hint">
                  {Icon.sparkle}<span>AI answers are personalized using this resume</span>
                </p>
                {resume.summary && (
                  <button className="pd-resume-toggle" onClick={() => setShowSummary(s => !s)}>
                    {showSummary ? 'Hide summary' : 'View extracted summary'}
                  </button>
                )}
                {showSummary && resume.summary && (
                  <div className="pd-resume-summary">{resume.summary}</div>
                )}
                <div className="pd-resume-actions">
                  <button className="pd-btn pd-btn-ghost pd-resume-btn" onClick={() => fileInputRef.current?.click()} disabled={uploadingResume}>
                    {Icon.upload}<span>Replace</span>
                  </button>
                  <button className="pd-resume-remove" onClick={handleResumeDelete} title="Remove resume">
                    {Icon.trash}
                  </button>
                </div>
              </>
            ) : (
              <div className="pd-resume-empty">
                <div className="pd-resume-drop" onClick={() => fileInputRef.current?.click()}>
                  <div className="pd-resume-drop-icon">{uploadingResume ? <span className="pd-spinner" /> : Icon.upload}</div>
                  <p className="pd-resume-drop-title">{uploadingResume ? 'Parsing resume...' : 'Upload resume'}</p>
                  <p className="pd-resume-drop-sub">PDF, DOCX or TXT · max 5MB</p>
                </div>
                <p className="pd-resume-hint">
                  {Icon.sparkle}<span>We'll tailor AI answers to the candidate's real experience</span>
                </p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              style={{ display: 'none' }}
              onChange={handleResumeUpload}
            />
          </div>
        </aside>

        {/* Right: interviews */}
        <section className="pd-main">
          <div className="pd-section-header">
            <div>
              <h2>Interview Prep</h2>
              <p>Saved sessions grouped by company and round</p>
            </div>
            <button className={`pd-btn ${showAddInterview ? 'pd-btn-ghost' : 'pd-btn-primary'}`} onClick={() => setShowAddInterview(!showAddInterview)}>
              {showAddInterview ? <>{Icon.close}<span>Cancel</span></> : <>{Icon.plus}<span>New Interview</span></>}
            </button>
          </div>

          {showAddInterview && (
            <div className="pd-add-form">
              <form onSubmit={handleAddInterview}>
                <div className="pd-field">
                  <label>Company Name <span className="pd-req">*</span></label>
                  <input type="text" value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    placeholder="e.g., Google, Amazon, Microsoft" required />
                </div>
                <div className="pd-field">
                  <label>Interview Round <span className="pd-req">*</span></label>
                  <div className="pd-chip-grid">
                    {ROUNDS.map(round => (
                      <button type="button" key={round}
                        className={`pd-round-chip ${formData.interviewRound === round ? 'active' : ''}`}
                        onClick={() => setFormData({ ...formData, interviewRound: round })}>
                        {round}
                      </button>
                    ))}
                  </div>
                </div>
                <button type="submit" className="pd-btn pd-btn-primary pd-start" disabled={isLoading}>
                  <span>{isLoading ? 'Starting...' : 'Start Interview Prep'}</span>{Icon.arrowRight}
                </button>
              </form>
            </div>
          )}

          {isLoading && !showAddInterview ? (
            <div className="pd-loading"><span className="pd-spinner" />Loading interviews...</div>
          ) : interviews.length === 0 ? (
            <div className="pd-empty">
              <div className="pd-empty-icon">{Icon.fileText}</div>
              <h3>No interviews yet</h3>
              <p>Create your first interview prep session to get started</p>
              {!showAddInterview && (
                <button className="pd-btn pd-btn-primary" onClick={() => setShowAddInterview(true)}>
                  {Icon.plus}<span>New Interview</span>
                </button>
              )}
            </div>
          ) : (
            <div className="pd-interviews-grid">
              {interviews.map((interview, idx) => (
                <div key={idx} className="pd-interview-card" onClick={() => handleSelectInterview(interview.companyName, interview.interviewRound)}>
                  <div className="pd-interview-top">
                    <div className="pd-company-icon">{Icon.building}</div>
                    <span className="pd-company-name">{interview.companyName}</span>
                    {interview.count === 0 && (
                      <button className="pd-interview-del" title="Remove interview"
                        onClick={(e) => handleRemoveInterview(e, interview.companyName, interview.interviewRound)}>
                        {Icon.trash}
                      </button>
                    )}
                  </div>
                  <h3 className="pd-round">{interview.interviewRound}</h3>
                  <p className="pd-round-count">{interview.count} question{interview.count !== 1 ? 's' : ''} saved</p>
                  <div className="pd-open">Open{Icon.arrowRight}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default ProfileDetail;
